import React, { useState, useContext } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography, COLORS, Tooltip, Button, IconButton, Tag,
  Table, TableContainer, TableHead, TableBody, TableRow, TableCell,
} from 'pvs-design-system';
import { Box, Stack, Divider } from '@mui/material';
import BootConfigContext from 'components/BootConfigContext/BootConfigContext';
import TourModal from 'components/virtual-library/TourModal';
import DeleteResourceModal from 'components/virtual-library/DeleteResourceModal';
import AdvancedSearchFiltersModal from 'components/virtual-library/AdvancedSearchFiltersModal';
import DetailsPanel from 'components/virtual-library/DetailsPanel';
import LibraryErrorBoundary from 'components/virtual-library/LibraryErrorBoundary';
import PaginationControls from 'components/virtual-library/PaginationControls';
import HighlightText from 'components/virtual-library/HighlightText';
import { useGetResourcesQuery, useDeleteResourceMutation } from 'api/useLibraryApi';
import { useModalNavigate, useTour } from 'hooks';
import { Notify } from 'components/Notifications/Notifications';
import { extractErrorMessage } from 'utils/errorUtils';
import {
  canManageLibrary,
  canModifyCategory,
  canDeleteCategory,
  canModifyResource,
  canDeleteResource,
} from 'utils/permissionHelper';
import { encodeQueryValue } from 'utils/urlEncoding';
import { ROUTES } from 'constants/constants';
import { getItemTypeLabel } from 'utils/fileUtils';
import { Resource } from 'types';
import { SearchCategoryMatch } from 'types/LibraryApi';
import Folder from '@mui/icons-material/Folder';
import Search from '@mui/icons-material/Search';
import Info from '@mui/icons-material/Info';
import Edit from '@mui/icons-material/Edit';
import Delete from '@mui/icons-material/Delete';
import LibraryHeader from 'components/virtual-library/LibraryHeader';
import LibrarySearchBar, {
  type SearchInValue, type SearchMode,
} from 'components/virtual-library/LibrarySearchBar';
import {
  LibraryViewToggle,
  LibraryTableSkeleton,
  type ViewMode,
} from 'components/virtual-library/library-content';
import { LIBRARY_ROW_SX, LIBRARY_GRID_CARD_SX } from 'constants/styles';

const PAGE_SIZE = 25;

/**
 * Derives the PARENT lineage of a searched category from its full path, matching
 * the lineage the browse/home views pass to the Edit/Delete folder modals.
 * e.g. '/Home/Science/Math/' → '/Home/Science/'
 */
function parentLineageOf(fullPath: string): string {
  const parts = fullPath.split('/').filter(Boolean);
  const parent = parts.slice(0, -1);
  return parent.length > 0 ? `/${parent.join('/')}/` : '/Home/';
}

/**
 * Converts a category fullPath into a human-readable parent breadcrumb so
 * users can distinguish same-named folders in search results.
 *
 * Examples
 *   '/Home/State Testing/'                              → 'Virtual Library'
 *   '/Home/My School: Maine/State Testing/'             → 'My School: Maine'
 *   '/Home/My School: AZ/State Testing Info/Locations/' → 'My School: AZ › State Testing Info'
 */
function parentBreadcrumb(fullPath: string): string {
  const segments = fullPath.split('/').filter(Boolean);
  // segments[0] = 'Home'  |  segments[-1] = the category name itself
  const ancestors = segments.slice(1, -1); // strip 'Home' and the category's own name
  if (ancestors.length === 0) return 'Virtual Library';
  return ancestors.join(' › ');
}

const SearchResultsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const fromLineage = searchParams.get('from');
  const navigate = useNavigate();
  const openModal = useModalNavigate();

  const bootConfig = useContext(BootConfigContext);
  const { permissions } = bootConfig.authInfo;
  const canManage = canManageLibrary(permissions);
  const isManageModeActive = canManage;

  const { isTourOpen, tourCompleted, openTour, skipTour, completeTour } = useTour();

  const [searchMode, setSearchMode] = useState<SearchMode>(() => (searchParams.get('searchMode') as SearchMode) ?? 'all');
  const [searchIn, setSearchIn] = useState<SearchInValue>(() => (searchParams.get('searchIn') as SearchInValue) ?? 'all');
  const [page, setPage] = useState(1);
  const [selectedItem, setSelectedItem] = useState<Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');

  const handleSearchInChange = (v: SearchInValue) => {
    setSearchIn(v);
    setPage(1);
  };

  const handleSearchModeChange = (v: SearchMode) => {
    setSearchMode(v);
    setPage(1);
  };

  const effectiveSearchIn: Array<'category' | 'name' | 'description'> =
    searchIn === 'all'
      ? ['category', 'name', 'description']
      : [searchIn as 'category' | 'name' | 'description'];

  const { data: resourcesData, searchCategoryMatches, isLoading } = useGetResourcesQuery(
    { q: q || undefined, searchMode, searchIn: effectiveSearchIn, isManageMode: isManageModeActive },
    { skip: !q }
  );

  const [deleteResource] = useDeleteResourceMutation();

  const allResources: Resource[] = resourcesData?.items ?? [];
  const totalPages = Math.max(1, Math.ceil(allResources.length / PAGE_SIZE));
  const pagedResources = allResources.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const categoryMatches: SearchCategoryMatch[] = searchCategoryMatches ?? [];
  const totalResults = categoryMatches.length + allResources.length;

  const clearDestination = fromLineage
    ? `${ROUTES.browse}?lineage=${encodeQueryValue(fromLineage)}`
    : ROUTES.library;

  const handleSearch = (query: string) => {
    const trimmed = query.trim();
    if (trimmed) {
      const next = new URLSearchParams({ q: trimmed });
      if (fromLineage) next.set('from', fromLineage);
      setSearchParams(next);
      setPage(1);
    }
  };

  const handleResourceClick = (resource: Resource) => {
    window.open(resource.url, '_blank', 'noopener,noreferrer');
  };

  const handleShowDetails = (resource: Resource, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedItem(resource);
  };

  const handleFolderClick = (match: SearchCategoryMatch, e?: React.MouseEvent) => {
    // Keep backend-provided fullPath intact for lineage routing/API semantics.
    const folderUrl = `${ROUTES.browse}?lineage=${encodeQueryValue(match.fullPath)}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(folderUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(folderUrl);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id: deletedId, title } = deleteTarget;
    try {
      await deleteResource(deletedId).unwrap();
      Notify.success(`Resource "${title}" deleted.`);
      // Close the details drawer if it was showing the just-deleted resource,
      // otherwise it lingers with stale content.
      if (selectedItem?.id === deletedId) setSelectedItem(null);
    } catch (e) {
      Notify.error(extractErrorMessage(e, 'Failed to delete resource. Please try again.'));
    } finally {
      setDeleteTarget(null);
    }
  };

  // Aliases to the shared style constants — search rows keep their own markup
  // (Folder column + highlighting) but reuse the browse pages' hover/card styles.
  const rowSx = LIBRARY_ROW_SX;
  const gridCardSx = LIBRARY_GRID_CARD_SX;

  return (
    <>
      <Box component="main" sx={{ display: 'flex', flexDirection: 'column', background: COLORS.beige[200] }}>
        <LibraryHeader
          tourCompleted={tourCompleted}
          onTour={openTour}
        />

        {/* Main content: hero + table on the left, details panel on the right */}
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            {/* Hero section: search + breadcrumbs (mirrors IndexPage/SubdirectoryPage) */}
            <Box>
              <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 6, px: 4, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography
                  variant="h2"
                  component="h1"
                  style={{ color: COLORS.gray[900], marginBottom: 20, textAlign: 'left', fontWeight: 700 }}
                >
                  Virtual Library
                </Typography>

                <Box sx={{ width: '100%', maxWidth: 740 }}>
                  <LibrarySearchBar
                    value={q}
                    onSearch={handleSearch}
                    onClear={() => navigate(clearDestination)}
                    size="large"
                    placeholder="Search Virtual Library"
                    settingsOpen={settingsOpen}
                    onSettingsToggle={() => setSettingsOpen((v) => !v)}
                  />
                </Box>
              </Box>

            </Box>

            {/* Browse section */}
            <Box id="browse-section" sx={{ maxWidth: 1100, mx: 'auto', px: 4, mt: 4, mb: 6, width: '100%' }}>
              {/* Results metadata + view toggle */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                {q ? (
                  <Stack direction="row" alignItems="center" gap={1.5}>
                    <Typography variant="p-md-semiBold" style={{ color: COLORS.gray[900] }}>
                      {isLoading
                        ? `Searching for "${q}"…`
                        : `${totalResults} result${totalResults !== 1 ? 's' : ''} for "${q}"`}
                    </Typography>
                    <Button
                      variant="text"
                      color="primary"
                      size="small"
                      onClick={() => navigate(clearDestination)}
                      sx={{ textTransform: 'none', minWidth: 0, padding: 0 }}
                    >
                      Clear Search
                    </Button>
                  </Stack>
                ) : (
                  <span />
                )}
                <LibraryViewToggle viewMode={viewMode} onChange={setViewMode} />
              </Stack>

            <LibraryErrorBoundary>
              {viewMode === 'list' ? (
                <>
                  <TableContainer sx={{ border: `1px solid ${COLORS.gray[200]}`, borderRadius: 2 }}>
                    <Table
                      stickyHeader
                      size="small"
                      aria-label="Search results"
                      sx={{
                        tableLayout: 'fixed',
                        '& .MuiTableCell-root': { paddingTop: '8px', paddingBottom: '8px' },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ width: 100 }}>Type</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell sx={{ width: 200 }}>Folder</TableCell>
                          <TableCell sx={{ width: 124 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading ? (
                          <LibraryTableSkeleton />
                        ) : !q ? (
                          <TableRow>
                            <TableCell colSpan={4} sx={{ border: 'none' }}>
                              <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                <Search sx={{ fontSize: 40, color: COLORS.gray[200] }} />
                                <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>Enter a term to search the library</Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <>
                            {/* Category rows */}
                            {categoryMatches.map((match, i) => {
                              const catUrl = `${ROUTES.browse}?lineage=${encodeQueryValue(match.fullPath)}`;
                              return (
                                <TableRow
                                  key={`cat-${i}`}
                                  tabIndex={0}
                                  role="button"
                                  onClick={(e: React.MouseEvent) => handleFolderClick(match, e)}
                                  onKeyDown={(e: React.KeyboardEvent) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleFolderClick(match); } }}
                                  sx={rowSx}
                                >
                                  <TableCell align="center">
                                    <Box title="Folder" sx={{ width: 32, height: 32, borderRadius: 8, bgcolor: COLORS.blue[100], color: COLORS.blue[700], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mx: 'auto' }}>
                                      <Folder style={{ fontSize: 18 }} />
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box
                                      component={RouterLink}
                                      to={catUrl}
                                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                      sx={{
                                        display: 'flex', alignItems: 'center', minWidth: 0,
                                        textDecoration: 'none',
                                      }}
                                    >
                                      <Typography
                                        variant="interactive-sm"
                                        component="span"
                                        sx={{
                                          color: COLORS.gray[900],
                                          fontWeight: 400,
                                          lineHeight: 1.3,
                                          display: 'block',
                                          minWidth: 0,
                                          whiteSpace: 'nowrap',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          '&:hover': { color: COLORS.purple[700], textDecoration: 'underline' },
                                        }}
                                      >
                                        <HighlightText text={match.name} query={q} />
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography
                                      variant="p-sm"
                                      title={parentBreadcrumb(match.fullPath)}
                                      style={{
                                        color: COLORS.gray[400],
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: 'block',
                                      }}
                                    >
                                      {parentBreadcrumb(match.fullPath)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                      {isManageModeActive && canModifyCategory(permissions) && (
                                        <Tooltip title="Edit folder" placement="top" arrow>
                                          <Box component="span">
                                            <IconButton size="small" color="primary" aria-label="Edit folder" onClick={() => openModal(ROUTES.editCategory, { editCategory: match.name, editLineage: parentLineageOf(match.fullPath) })}>
                                              <Edit fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {isManageModeActive && canDeleteCategory(permissions) && (
                                        <Tooltip title="Delete folder" placement="top" arrow>
                                          <Box component="span">
                                            <IconButton size="small" color="primary" aria-label="Delete folder" onClick={() => openModal(ROUTES.deleteCategory, { categoryName: match.name, lineage: parentLineageOf(match.fullPath) })}>
                                              <Delete fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {categoryMatches.length > 0 && pagedResources.length > 0 && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ p: 0, border: 'none' }}>
                                  <Divider sx={{ borderColor: COLORS.gray[100] }} />
                                </TableCell>
                              </TableRow>
                            )}

                            {/* Resource rows */}
                            {pagedResources.map((resource) => (
                              <TableRow
                                key={resource.id}
                                tabIndex={0}
                                role="button"
                                onClick={() => handleResourceClick(resource)}
                                onKeyDown={(e: React.KeyboardEvent) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleResourceClick(resource); } }}
                                sx={selectedItem?.id === resource.id
                                  ? { ...rowSx, borderLeft: `3px solid ${COLORS.purple[700]}` }
                                  : { ...rowSx, borderLeft: '3px solid transparent' }}
                              >
                                <TableCell align="center">
                                  <Tag variant="default" label={getItemTypeLabel(resource.type)} size="small" />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="interactive-sm"
                                    sx={{
                                      color: COLORS.gray[900],
                                      lineHeight: 1.3,
                                      display: 'block',
                                      cursor: 'pointer',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      '&:hover': { color: COLORS.purple[700], textDecoration: 'underline' },
                                    }}
                                  >
                                    <HighlightText text={resource.title} query={q} />
                                  </Typography>
                                  {resource.description && (
                                    <Typography variant="p-sm" style={{ color: COLORS.gray[400], fontSize: 11, display: 'block', lineHeight: 1.4, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      <HighlightText text={resource.description} query={q} />
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="p-sm"
                                    title={resource.categoryId || undefined}
                                    style={{
                                      color: COLORS.gray[400],
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      display: 'block',
                                    }}
                                  >
                                    {resource.categoryId || '—'}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                      <Tooltip title="View details" placement="top" arrow>
                                        <Box component="span">
                                          <IconButton size="small" color="primary" aria-label={`View details for ${resource.title}`} onClick={(e) => handleShowDetails(resource, e)}>
                                            <Info fontSize="small" />
                                          </IconButton>
                                        </Box>
                                      </Tooltip>
                                      {isManageModeActive && resource.canModify && canModifyResource(permissions) && (
                                        <Tooltip title="Edit resource" placement="top" arrow>
                                          <Box component="span">
                                            <IconButton size="small" color="primary" aria-label="Edit resource" onClick={() => openModal(`${ROUTES.editResource}/${resource.id}`)}>
                                              <Edit fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>
                                      )}
                                      {isManageModeActive && resource.canDelete && canDeleteResource(permissions) && (
                                        <Tooltip title="Delete resource" placement="top" arrow>
                                          <Box component="span">
                                            <IconButton size="small" color="primary" aria-label="Delete resource" onClick={() => setDeleteTarget(resource)}>
                                              <Delete fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  </TableCell>
                              </TableRow>
                            ))}

                            {categoryMatches.length === 0 && allResources.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4} sx={{ border: 'none' }}>
                                  <Box sx={{ py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                                    <Search sx={{ fontSize: 40, color: COLORS.gray[200] }} />
                                    <Typography variant="p-md-semiBold" style={{ color: COLORS.gray[700] }}>No results found</Typography>
                                    <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>Try different keywords or adjust your filters</Typography>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {!isLoading && q && totalPages > 1 && (
                    <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
                  )}
                </>
              ) : (
                /* Grid view — the grid shell stays mounted across loading/empty/results
                   states so a new search only swaps its children, not the surrounding layout. */
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 1.5, p: 3 }}>
                    {isLoading ? (
                      [1, 2, 3, 4, 5, 6].map((i) => (
                        <Box key={i} sx={{ height: 120, borderRadius: 2, background: 'linear-gradient(90deg, #f5f5f5 25%, #fafafa 50%, #f5f5f5 75%)', backgroundSize: '200% 100%', animation: 'shimmer 1.4s infinite' }} />
                      ))
                    ) : !q ? (
                      <Box sx={{ gridColumn: '1 / -1', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Search sx={{ fontSize: 40, color: COLORS.gray[200] }} />
                        <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>Enter a term to search the library</Typography>
                      </Box>
                    ) : categoryMatches.length === 0 && allResources.length === 0 ? (
                      <Box sx={{ gridColumn: '1 / -1', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                        <Search sx={{ fontSize: 40, color: COLORS.gray[200] }} />
                        <Typography variant="p-md-semiBold" style={{ color: COLORS.gray[700] }}>No results found</Typography>
                        <Typography variant="p-sm" style={{ color: COLORS.gray[400] }}>Try different keywords or adjust your filters</Typography>
                      </Box>
                    ) : (
                      <>
                          {categoryMatches.map((match, i) => (
                        <Box
                          key={`cat-${i}`}
                          role="button"
                          tabIndex={0}
                          onClick={(e: React.MouseEvent) => handleFolderClick(match, e)}
                          onKeyDown={(e: React.KeyboardEvent) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleFolderClick(match); } }}
                          sx={gridCardSx}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: COLORS.blue[100], color: COLORS.blue[700], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <Folder style={{ fontSize: 20 }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="interactive-sm" style={{ fontWeight: 600, color: COLORS.gray[900], display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35 }}>
                                {match.name}
                              </Typography>
                              <Typography
                                variant="p-sm"
                                title={parentBreadcrumb(match.fullPath)}
                                style={{
                                  color: COLORS.gray[400],
                                  fontSize: 11,
                                  marginTop: 2,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: 'block',
                                }}
                              >
                                {parentBreadcrumb(match.fullPath)}
                              </Typography>
                            </Box>
                          </Box>
                          {isManageModeActive && (canModifyCategory(permissions) || canDeleteCategory(permissions)) && (
                            <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
                                {canModifyCategory(permissions) && (
                                  <Tooltip title="Edit folder" placement="top" arrow>
                                    <Box component="span">
                                      <IconButton size="small" color="primary" className="info-reveal" sx={{ opacity: 0, transition: 'opacity 0.15s ease' }} aria-label="Edit folder" onClick={() => openModal(ROUTES.editCategory, { editCategory: match.name, editLineage: parentLineageOf(match.fullPath) })}>
                                        <Edit fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                )}
                                {canDeleteCategory(permissions) && (
                                  <Tooltip title="Delete folder" placement="top" arrow>
                                    <Box component="span">
                                      <IconButton size="small" color="primary" className="info-reveal" sx={{ opacity: 0, transition: 'opacity 0.15s ease' }} aria-label="Delete folder" onClick={() => openModal(ROUTES.deleteCategory, { categoryName: match.name, lineage: parentLineageOf(match.fullPath) })}>
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      ))}
                      {pagedResources.map((resource) => (
                        <Box
                          key={resource.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleResourceClick(resource)}
                          onKeyDown={(e: React.KeyboardEvent) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleResourceClick(resource); } }}
                          sx={selectedItem?.id === resource.id
                            ? { ...gridCardSx, border: `1.5px solid ${COLORS.purple[700]}`, bgcolor: 'rgba(106,27,131,0.03)' }
                            : gridCardSx}
                        >
                          <Typography variant="interactive-sm" style={{ fontWeight: 600, color: COLORS.gray[900], display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.35, marginBottom: 4 }}>
                            <HighlightText text={resource.title} query={q} />
                          </Typography>
                          {resource.description && (
                            <Typography variant="p-sm" style={{ color: COLORS.gray[400], fontSize: 11, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4, marginBottom: 6 }}>
                              <HighlightText text={resource.description} query={q} />
                            </Typography>
                          )}
                          <Box sx={{ mt: 'auto', pt: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Tag variant="default" label={getItemTypeLabel(resource.type)} size="small" />
                            <Stack direction="row" gap={0.5} onClick={(e) => e.stopPropagation()}>
                              <IconButton
                                size="small"
                                color="primary"
                                className="info-reveal"
                                sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}
                                onClick={(e: React.MouseEvent) => handleShowDetails(resource, e)}
                                aria-label={`View details for ${resource.title}`}
                              >
                                <Info fontSize="small" />
                              </IconButton>
                              {isManageModeActive && resource.canModify && canModifyResource(permissions) && (
                                <Tooltip title="Edit resource" placement="top" arrow>
                                  <Box component="span">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      className="info-reveal"
                                      sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}
                                      aria-label="Edit resource"
                                      onClick={() => openModal(`${ROUTES.editResource}/${resource.id}`)}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              )}
                              {isManageModeActive && resource.canDelete && canDeleteResource(permissions) && (
                                <Tooltip title="Delete resource" placement="top" arrow>
                                  <Box component="span">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      className="info-reveal"
                                      sx={{ opacity: 0, transition: 'opacity 0.15s ease' }}
                                      aria-label="Delete resource"
                                      onClick={() => setDeleteTarget(resource)}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Tooltip>
                              )}
                            </Stack>
                          </Box>
                        </Box>
                      ))}
                      </>
                    )}
                  </Box>

                  {!isLoading && q && totalPages > 1 && (
                    <PaginationControls page={page} totalPages={totalPages} onPageChange={setPage} />
                  )}
                </>
              )}
            </LibraryErrorBoundary>
            </Box>
          </Box>

          <DetailsPanel item={selectedItem} onClose={() => setSelectedItem(null)} canViewStatistics={canManage} />
        </Box>
      </Box>

      <TourModal canManage={canManage} isOpen={isTourOpen} onSkip={skipTour} onFinish={completeTour} />
      <DeleteResourceModal open={deleteTarget !== null} resourceTitle={deleteTarget?.title} onConfirm={handleDeleteConfirm} onCancel={() => setDeleteTarget(null)} />
      <AdvancedSearchFiltersModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        searchIn={searchIn}
        onSearchInChange={handleSearchInChange}
        searchMode={searchMode}
        onSearchModeChange={handleSearchModeChange}
      />
    </>
  );
};

export default SearchResultsPage;
